import { useState, useCallback } from 'react'
import styled from 'styled-components'
import { tokens } from '../../styles/tokens'
import { API_GROUPS, Endpoint, Method, Role } from './endpoints'
import { PageHeader, PageMeta, PageEyebrow, PageTitle } from '../../components/ui/Table'

// ─── Method badge ─────────────────────────────────────────
const METHOD_COLOR: Record<Method, { bg: string; text: string }> = {
  GET: { bg: '#e8f5e9', text: '#2e7d32' },
  POST: { bg: '#e3f2fd', text: '#1565c0' },
  PUT: { bg: '#fff3e0', text: '#e65100' },
  PATCH: { bg: '#fef9c3', text: '#854d0e' },
  DELETE: { bg: '#fce4e4', text: '#c62828' },
}

const ROLE_COLOR: Record<Role, { bg: string; text: string }> = {
  PUBLIC: { bg: tokens.purple100, text: tokens.purple400 },
  ROLE_USER: { bg: '#e8f5e9', text: '#2e7d32' },
  ROLE_SUPPORT: { bg: '#fff3e0', text: '#e65100' },
  ROLE_ADMIN: { bg: '#f3e8ff', text: '#6b21a8' },
}

const ROLE_LABEL: Record<Role, string> = {
  PUBLIC: 'Public',
  ROLE_USER: 'User',
  ROLE_SUPPORT: 'Support',
  ROLE_ADMIN: 'Admin',
}

// ─── Styled components ────────────────────────────────────
const PageBody = styled.div`
  padding: 0 32px 48px;
  max-width: 900px;
`

const Group = styled.section`
  margin-bottom: 40px;
`

const GroupHeader = styled.div`
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${tokens.purple100};
`

const GroupName = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: ${tokens.purple900};
  margin-bottom: 4px;
`

const GroupDesc = styled.p`
  font-size: 12px;
  color: ${tokens.purple400};
  line-height: 1.5;
`

const EndpointCard = styled.div`
  border: 1px solid ${tokens.purple100};
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
`

const EndpointHeader = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${p => (p.$open ? tokens.purple50 : 'white')};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${tokens.purple50};
  }
`

const MethodBadge = styled.span<{ $method: Method }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 3px 7px;
  border-radius: 5px;
  flex-shrink: 0;
  font-family: monospace;
  background: ${p => METHOD_COLOR[p.$method].bg};
  color: ${p => METHOD_COLOR[p.$method].text};
  min-width: 52px;
  text-align: center;
`

const EndpointPath = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: ${tokens.purple700};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EndpointDesc = styled.span`
  font-size: 11px;
  color: ${tokens.purple400};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
`

const RoleBadge = styled.span<{ $role: Role }>`
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 4px;
  flex-shrink: 0;
  background: ${p => ROLE_COLOR[p.$role].bg};
  color: ${p => ROLE_COLOR[p.$role].text};
`

const Chevron = styled.span<{ $open: boolean }>`
  font-size: 10px;
  color: ${tokens.purple400};
  flex-shrink: 0;
  transform: ${p => (p.$open ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.2s;
`

const EndpointBody = styled.div`
  padding: 16px;
  border-top: 1px solid ${tokens.purple100};
  background: #fafaf9;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const BodySection = styled.div``

const BodyLabel = styled.p`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${tokens.purple400};
  margin-bottom: 6px;
`

const CodeBlock = styled.pre`
  background: ${tokens.purple900};
  color: #e2d9f3;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 11px;
  line-height: 1.6;
  overflow-x: auto;
  margin: 0;
`

const ParamsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
`

const ParamRow = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${tokens.purple100};
  }
`

const ParamCell = styled.td`
  padding: 6px 8px;
  vertical-align: top;
  color: ${tokens.purple700};

  &:first-child {
    font-family: monospace;
    color: ${tokens.purple900};
    white-space: nowrap;
  }
`

const ParamType = styled.span`
  color: ${tokens.purple400};
  font-size: 10px;
`

const RequiredBadge = styled.span`
  font-size: 9px;
  background: #fce4e4;
  color: #c62828;
  border-radius: 3px;
  padding: 1px 5px;
  margin-left: 4px;
  font-weight: 500;
`

const EmptyJson = styled.span`
  font-size: 11px;
  color: ${tokens.purple400};
  font-style: italic;
`

// ─── Endpoint row ─────────────────────────────────────────
function EndpointRow({ ep }: { ep: Endpoint }) {
  const [open, setOpen] = useState(false)
  const toggle = useCallback(() => setOpen(v => !v), [])

  return (
    <EndpointCard>
      <EndpointHeader $open={open} onClick={toggle}>
        <MethodBadge $method={ep.method}>{ep.method}</MethodBadge>
        <EndpointPath>{ep.path}</EndpointPath>
        <EndpointDesc>{ep.description}</EndpointDesc>
        {ep.roles.map(r => (
          <RoleBadge key={r} $role={r}>
            {ROLE_LABEL[r]}
          </RoleBadge>
        ))}
        <Chevron $open={open}>▼</Chevron>
      </EndpointHeader>

      {open && (
        <EndpointBody>
          <BodySection>
            <BodyLabel>Description</BodyLabel>
            <p style={{ fontSize: 12, color: tokens.purple700, lineHeight: 1.5 }}>
              {ep.description}
            </p>
          </BodySection>

          {ep.queryParams && ep.queryParams.length > 0 && (
            <BodySection>
              <BodyLabel>Query Parameters</BodyLabel>
              <ParamsTable>
                <tbody>
                  {ep.queryParams.map(p => (
                    <ParamRow key={p.name}>
                      <ParamCell>
                        {p.name}
                        {p.required && <RequiredBadge>required</RequiredBadge>}
                      </ParamCell>
                      <ParamCell>
                        <ParamType>{p.type}</ParamType>
                      </ParamCell>
                      <ParamCell style={{ color: tokens.purple400 }}>{p.description}</ParamCell>
                    </ParamRow>
                  ))}
                </tbody>
              </ParamsTable>
            </BodySection>
          )}

          {ep.requestBody !== undefined && (
            <BodySection>
              <BodyLabel>Request Body</BodyLabel>
              {ep.requestBody === null ? (
                <EmptyJson>No body</EmptyJson>
              ) : (
                <CodeBlock>{JSON.stringify(ep.requestBody, null, 2)}</CodeBlock>
              )}
            </BodySection>
          )}

          <BodySection>
            <BodyLabel>Example Response</BodyLabel>
            {ep.responseBody === null ? (
              <EmptyJson>204 No Content</EmptyJson>
            ) : (
              <CodeBlock>{JSON.stringify(ep.responseBody, null, 2)}</CodeBlock>
            )}
          </BodySection>
        </EndpointBody>
      )}
    </EndpointCard>
  )
}

// ─── Page ─────────────────────────────────────────────────
export function ApiDocsPage() {
  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>Developer</PageEyebrow>
          <PageTitle>API Reference</PageTitle>
        </PageMeta>
      </PageHeader>

      <PageBody>
        {API_GROUPS.map(group => (
          <Group key={group.name}>
            <GroupHeader>
              <GroupName>{group.name}</GroupName>
              <GroupDesc>{group.description}</GroupDesc>
            </GroupHeader>
            {group.endpoints.map(ep => (
              <EndpointRow key={`${ep.method}-${ep.path}`} ep={ep} />
            ))}
          </Group>
        ))}
      </PageBody>
    </>
  )
}
